import { useInfiniteQuery } from '@tanstack/react-query';
import { ComponentProps } from 'preact/compat';

import { loadPage } from '@/helpers';
import { cn } from '@/lib/utils';
import { getInfiniteMediaQueryOptions, InfiniteMediaQuerySearchParams } from '@/queries/media';

interface HabitatMediaGridProps extends ComponentProps<'ul'> {
  mediaQueryParams: InfiniteMediaQuerySearchParams;
  pageSize: number;
  tileClassName?: string;
  /**
   * Off for hosts that are themselves a media page: there, a tile means "show me this one instead",
   * so it replaces the whole page — the top-level one, since these grids are usually inside a frame.
   */
  openInNewTab?: boolean;
}

const HabitatMediaGrid = ({
  mediaQueryParams,
  pageSize,
  className,
  tileClassName,
  openInNewTab = true,
  ...props
}: HabitatMediaGridProps) => {
  const { data, isLoading } = useInfiniteQuery(getInfiniteMediaQueryOptions(mediaQueryParams));

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <ul className={cn('preflight preflight-ul empty:hidden', className)} {...props}>
      {data?.list.slice(0, pageSize).map((media) => {
        const href = `/media/${media.slugPrefix || 'media'}-${media._id}`;
        return (
          <li key={media._id}>
            <a
              href={href}
              // eslint-disable-next-line react/jsx-no-target-blank
              target={openInNewTab ? '_blank' : undefined}
              // Two opt-outs, and both are needed: `data-native` stops preact-router and the
              // embedded page's own anchor delegate from hijacking a same-origin href, while the
              // handler escapes the frame — a plain anchor would only navigate the frame itself.
              data-native={!openInNewTab || undefined}
              onClick={
                openInNewTab
                  ? undefined
                  : (event) => {
                      // Let the browser handle modified clicks so "open in a new tab" still works.
                      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                        return;
                      }
                      event.preventDefault();
                      loadPage(href);
                    }
              }
              className="preflight preflight-a block"
              rel="noreferrer"
            >
              <div className={cn('preflight relative overflow-hidden rounded desktop:rounded-lg', tileClassName)}>
                <img
                  className="preflight preflight-img aspect-video"
                  src={media.thumbnailURL}
                  alt={media.title}
                  loading="lazy"
                />
              </div>
            </a>
          </li>
        );
      })}
      {isLoading &&
        Array.from({ length: pageSize }).map((_, index) => (
          <li key={`placeholder-${index.toString()}`}>
            <div
              className={cn(
                'preflight skeleton relative aspect-video overflow-hidden rounded bg-grey-5 desktop:rounded-lg',
                tileClassName,
              )}
            />
          </li>
        ))}
    </ul>
  );
};

export default HabitatMediaGrid;
